import React from "react";
import { Link } from "react-router-dom";
import SEO from "../../../components/SEO";
import img44 from "../../../assets/img44.jpeg";
import { CheckCircle } from "lucide-react";
import img10 from "../../../assets/img10.jpg";
import BlogSection from "../../../components/BlogSection";

export default function TaxFreeSavingsAccount() {
  const container = "w-full max-w-[1380px] mx-auto";

  return (
    <>
      <SEO
        title="Compte d'épargne libre d'impôt (CELI) – einsured.ca"
        description="Découvrez le CELI chez einsured.ca : cotisations, avantages fiscaux et options de placement pour faire fructifier votre épargne à l’abri de l’impôt au Canada."
        keywords="CELI Canada, limite CELI 2024, CELI vs REER, croissance d'investissement libre d'impôt Canada"
      />
      {/* HERO SECTION */}
      <section
        className="relative min-h-[60vh] sm:min-h-[70vh] md:h-[80vh] bg-cover bg-center flex items-center justify-center md:justify-start px-4 sm:px-6 md:px-20 text-center md:text-left"
        style={{ backgroundImage: `url(${img44})` }}
      >
        <div className="absolute inset-0 bg-black/50"></div>
        <div className={container}>
          <div className="relative z-10 text-white max-w-3xl">
            <h1 className="text-3xl sm:text-5xl md:text-6xl font-bold mb-4 leading-tight">
              Compte d'épargne
              <br className="hidden sm:block" />
              libre d'impôt
            </h1>

            <p className="text-base sm:text-xl font-medium mb-6">
              Faites croître votre épargne à l'abri de l'impôt avec un accès
              facile à vos fonds et des placements qui fructifient plus rapidement
            </p>

            <a
              href="https://book.einsured.ca/widget/bookings/ei-team"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block w-full sm:w-auto bg-sky-400 hover:bg-sky-500 text-white font-semibold px-8 py-3 rounded-full transition shadow-md text-base sm:text-lg"
            >
              Investir maintenant
            </a>
          </div>
        </div>
      </section>

      {/* QU'EST-CE QUE LE CELI */}
      <section className="py-16 sm:py-18 bg-white px-4 sm:px-6 md:px-20 text-gray-800">
        <div className="max-w-[1380px] mx-auto">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#49628b] text-center mb-6">
            Qu'est-ce que le CELI ?
          </h2>

          <p className="text-base sm:text-lg leading-7 sm:leading-8 mb-5">
            Le{" "}
            <a
              href="https://www.canada.ca/en/revenue-agency/services/forms-publications/publications/rc4466/tax-free-savings-account-tfsa-guide-individuals.html"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#20a7df] font-medium underline hover:opacity-90"
            >
              Compte d'épargne libre d'impôt (CELI)
            </a>{" "}
            est un excellent programme pour les Canadiens qui vous permet d'épargner
            à tout moment de votre vie adulte. Les revenus générés dans votre CELI,
            tels que les intérêts, les dividendes et les gains en capital, ne seront
            pas imposés. Ils vous appartiennent et vous pouvez les utiliser comme
            bon vous semble. Vous pouvez utiliser cet argent pour la retraite, pour
            rénover votre maison, pour envoyer votre enfant à l'université ou pour
            voyager vers une destination exotique ! C'est une excellente façon de
            faire fructifier votre épargne.
          </p>

          <p className="text-base sm:text-lg leading-7 sm:leading-8 mb-5">
            Les cotisations versées régulièrement peuvent rapidement se transformer
            en une somme beaucoup plus importante. Beaucoup de personnes sont
            surprises de constater à quel point leur épargne augmente au fil de la
            croissance de leur compte !
          </p>

          <p className="text-base sm:text-lg leading-7 sm:leading-8 mb-5">
            Avec un{" "}
            <span className="font-semibold text-[#20a7df]">
              Compte d'épargne libre d'impôt ou CELI
            </span>
            , vous avez la possibilité de choisir parmi une variété de placements
            financiers. Vous pouvez investir dans des fonds négociés en bourse, des
            certificats de placement garanti, des actions, des obligations et un
            compte d'épargne. Tous les gains que vous réalisez sont libres d'impôt.
            Cela donne aux investisseurs ordinaires la possibilité de maximiser leurs
            rendements. De plus, le Compte d'épargne libre d'impôt vous offre la
            flexibilité de retirer votre épargne à tout moment et de dépenser cet
            argent judicieusement sans vous soucier des obligations fiscales.
          </p>

          <p className="text-base sm:text-lg leading-7 sm:leading-8">
            De plus, le CELI n'a aucune incidence négative sur vos prestations
            gouvernementales. Pour maximiser votre patrimoine, nos experts peuvent
            vous recommander un large éventail de possibilités de placement.
          </p>
        </div>
      </section>

      {/* RAISONS DE COTISER AU CELI */}
      <section className="py-16 sm:py-2 bg-white px-4 sm:px-6 md:px-20 text-gray-800">
        <div className="max-w-[1380px] mx-auto">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#49628b] text-center mb-6">
            Raisons de cotiser à un compte d'épargne libre d'impôt :
          </h2>

          <p className="text-base sm:text-lg leading-7 sm:leading-8 mb-8 text-center">
            Cotiser à un{" "}
            <a
              href="https://www.canada.ca/en/revenue-agency/services/forms-publications/publications/rc4466/tax-free-savings-account-tfsa-guide-individuals.html"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#20a7df] font-medium underline hover:opacity-90"
            >
              Compte d'épargne libre d'impôt (CELI)
            </a>{" "}
            au Canada présente plusieurs avantages, ce qui en fait une option
            attrayante pour les personnes souhaitant faire croître leur épargne.
          </p>

          <h3 className="text-xl sm:text-2xl font-bold text-[#49628b] mb-8 text-center">
            Voici quelques raisons convaincantes de cotiser à un CELI :
          </h3>

          {/* Reasons List */}
          <ul className="space-y-6">
            <li className="flex items-start gap-3">
              <CheckCircle
                aria-hidden="true"
                className="h-6 w-6 text-sky-500 mt-1 flex-shrink-0"
                strokeWidth={2.4}
              />
              <p className="text-base sm:text-lg leading-relaxed">
                <span className="font-bold text-[#49628b]">
                  Croissance libre d'impôt :
                </span>{" "}
                Les revenus et les gains en capital dans un CELI ne sont pas
                assujettis à l'impôt sur le revenu. Cela signifie que toute
                croissance des placements, les intérêts ou les dividendes générés
                dans le compte sont libres d'impôt.
              </p>
            </li>

            <li className="flex items-start gap-3">
              <CheckCircle
                aria-hidden="true"
                className="h-6 w-6 text-sky-500 mt-1 flex-shrink-0"
                strokeWidth={2.4}
              />
              <p className="text-base sm:text-lg leading-relaxed">
                <span className="font-bold text-[#49628b]">
                  Retraits libres d'impôt :
                </span>{" "}
                Contrairement aux comptes d'épargne ou de placement traditionnels,
                les retraits d'un CELI sont libres d'impôt. Cette flexibilité vous
                permet d'accéder à votre argent quand vous en avez besoin sans
                engager de taxes supplémentaires.
              </p>
            </li>

            <li className="flex items-start gap-3">
              <CheckCircle
                aria-hidden="true"
                className="h-6 w-6 text-sky-500 mt-1 flex-shrink-0"
                strokeWidth={2.4}
              />
              <p className="text-base sm:text-lg leading-relaxed">
                <span className="font-bold text-[#49628b]">
                  Report des droits de cotisation inutilisés :
                </span>{" "}
                Les droits de cotisation inutilisés dans un CELI peuvent être
                reportés indéfiniment. Si vous ne cotisez pas le montant maximal
                autorisé au cours d'une année donnée, la portion inutilisée peut
                être ajoutée à vos droits de cotisation pour les années futures.
              </p>
            </li>

            <li className="flex items-start gap-3">
              <CheckCircle
                aria-hidden="true"
                className="h-6 w-6 text-sky-500 mt-1 flex-shrink-0"
                strokeWidth={2.4}
              />
              <p className="text-base sm:text-lg leading-relaxed">
                <span className="font-bold text-[#49628b]">
                  Large éventail d'options de placement :
                </span>{" "}
                Les fonds d'un CELI peuvent être investis dans divers instruments
                financiers, notamment des comptes d'épargne, des CPG, des actions,
                des obligations et des fonds communs de placement. Cette flexibilité
                vous permet d'adapter vos placements à votre tolérance au risque et
                à vos objectifs financiers.
              </p>
            </li>

            <li className="flex items-start gap-3">
              <CheckCircle
                aria-hidden="true"
                className="h-6 w-6 text-sky-500 mt-1 flex-shrink-0"
                strokeWidth={2.4}
              />
              <p className="text-base sm:text-lg leading-relaxed">
                <span className="font-bold text-[#49628b]">
                  Aucune incidence sur les prestations gouvernementales :
                </span>{" "}
                Les retraits d'un CELI n'affectent pas l'admissibilité aux
                prestations et crédits gouvernementaux, tels que la Sécurité de la
                vieillesse (SV), le Supplément de revenu garanti (SRG) ou
                l'Allocation canadienne pour enfants (ACE).
              </p>
            </li>

            <li className="flex items-start gap-3">
              <CheckCircle
                aria-hidden="true"
                className="h-6 w-6 text-sky-500 mt-1 flex-shrink-0"
                strokeWidth={2.4}
              />
              <p className="text-base sm:text-lg leading-relaxed">
                <span className="font-bold text-[#49628b]">
                  Idéal pour les objectifs à court et à long terme :
                </span>{" "}
                Que vous épargniez pour un objectif à court terme comme des
                vacances ou un objectif à long terme comme la retraite, un CELI
                constitue un véhicule d'épargne polyvalent offrant des avantages
                fiscaux dans les deux cas.
              </p>
            </li>

            <li className="flex items-start gap-3">
              <CheckCircle
                aria-hidden="true"
                className="h-6 w-6 text-sky-500 mt-1 flex-shrink-0"
                strokeWidth={2.4}
              />
              <p className="text-base sm:text-lg leading-relaxed">
                <span className="font-bold text-[#49628b]">
                  Croissance du fonds d'urgence :
                </span>{" "}
                Un CELI peut servir d'excellent endroit pour placer votre fonds
                d'urgence. La croissance libre d'impôt et l'accessibilité en font
                un choix stratégique pour les fonds pouvant être nécessaires dans
                des situations imprévues.
              </p>
            </li>

            <li className="flex items-start gap-3">
              <CheckCircle
                aria-hidden="true"
                className="h-6 w-6 text-sky-500 mt-1 flex-shrink-0"
                strokeWidth={2.4}
              />
              <p className="text-base sm:text-lg leading-relaxed">
                <span className="font-bold text-[#49628b]">
                  Aucune limite d'âge pour les cotisations :
                </span>{" "}
                Contrairement aux Régimes enregistrés d'épargne-retraite (REER),
                il n'y a aucune limite d'âge pour cotiser à un CELI. Vous pouvez
                continuer à cotiser tant que vous répondez aux critères
                d'admissibilité.
              </p>
            </li>

            <li className="flex items-start gap-3">
              <CheckCircle
                aria-hidden="true"
                className="h-6 w-6 text-sky-500 mt-1 flex-shrink-0"
                strokeWidth={2.4}
              />
              <p className="text-base sm:text-lg leading-relaxed">
                <span className="font-bold text-[#49628b]">
                  Avantages pour la planification successorale :
                </span>{" "}
                Les actifs d'un CELI peuvent être transférés à un époux ou
                conjoint de fait au décès sans déclencher d'impôt. Les CELI
                constituent donc un outil précieux pour la planification
                successorale.
              </p>
            </li>

            <li className="flex items-start gap-3">
              <CheckCircle
                aria-hidden="true"
                className="h-6 w-6 text-sky-500 mt-1 flex-shrink-0"
                strokeWidth={2.4}
              />
              <p className="text-base sm:text-lg leading-relaxed">
                <span className="font-bold text-[#49628b]">
                  Flexibilité des cotisations et des retraits :
                </span>{" "}
                Les cotisations au CELI ne sont pas liées au revenu gagné, ce qui
                offre une flexibilité pour les cotisations. De plus, les retraits
                peuvent être effectués à tout moment, à n'importe quelle fin, sans
                pénalité.
              </p>
            </li>
          </ul>

          <p className="text-base sm:text-lg leading-relaxed mt-8">
            Avant de prendre toute décision financière, il est conseillé de
            consulter un conseiller financier chez{" "}
            <a
              href="/"
              className="text-[#20a7df] font-medium underline hover:opacity-90"
            >
              einsured.ca
            </a>{" "}
            pour s'assurer que les cotisations au CELI s'alignent avec votre plan
            financier global et vos objectifs.
          </p>
        </div>
      </section>

      {/* COMMENT COTISER À UN CELI */}
      <section
        className="relative py-20 sm:py-24 md:py-20 px-4 sm:px-6 md:px-24 bg-cover bg-center lg:bg-fixed mt-16 sm:mt-20"
        style={{ backgroundImage: `url(${img10})` }}
      >
        <div className="absolute inset-0 bg-sky-900 bg-opacity-75"></div>

        <div className="relative z-10 max-w-[1380px] mx-auto text-white">
          <h2 className="text-3xl sm:text-4xl md:text-4xl font-bold text-center mb-10">
            Comment cotiser à un CELI :
          </h2>

          {/* Vérifier les plafonds de cotisation */}
          <div className="bg-white text-gray-900 rounded-[2.5rem] shadow-lg px-6 sm:px-10 py-8 sm:py-10 mb-10">
            <h3 className="text-xl sm:text-2xl font-bold text-[#49628b] mb-3">
              Vérifier les plafonds de cotisation :
            </h3>

            <ul className="space-y-4 text-base sm:text-lg leading-relaxed">
              <li className="flex items-start gap-3">
                <p>
                  Assurez-vous de connaître votre{" "}
                  <a
                    href="https://www.canada.ca/en/revenue-agency/services/tax/individuals/topics/tax-free-savings-account/contributing.html"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#20a7df] font-medium underline hover:opacity-90"
                  >
                    plafond de cotisation annuel au CELI
                  </a>
                  , fixé par le gouvernement. Ce plafond s'accumule chaque année.
                </p>
              </li>

              <li className="flex items-start gap-3">
                <p>
                  Le plafond de cotisation au Compte d'épargne libre d'impôt (CELI)
                  est passé à 7 000 $ en 2024, contre 6 500 $ en 2023.
                </p>
              </li>

              <li className="flex items-start gap-3">
                <p>
                  Chaque année, l'Agence du revenu du Canada (ARC) déclare le
                  plafond de cotisation annuel au CELI, qui est indexé sur
                  l'inflation puis arrondi au 500 $ le plus proche.
                </p>
              </li>
            </ul>
          </div>

          {/* 3 cartes */}
          <div className="grid gap-6 sm:gap-8 md:grid-cols-3 mb-8">
            {/* Choisir vos placements */}
            <div className="bg-white text-gray-900 rounded-[2.5rem] shadow-lg px-6 sm:px-8 py-7 sm:py-8">
              <h3 className="text-xl sm:text-2xl font-bold text-[#49628b] mb-3">
                Choisir vos placements :
              </h3>
              <div className="flex items-start gap-3">
                <p className="text-base sm:text-lg leading-relaxed">
                  Décidez du type de placements que vous souhaitez détenir dans
                  votre CELI. Cela peut inclure des comptes d'épargne, des CPG,
                  des actions, des obligations ou des fonds communs de placement.
                </p>
              </div>
            </div>

            {/* Ouvrir un compte CELI */}
            <div className="bg-white text-gray-900 rounded-[2.5rem] shadow-lg px-6 sm:px-8 py-7 sm:py-8">
              <h3 className="text-xl sm:text-2xl font-bold text-[#49628b] mb-3">
                Ouvrir un compte CELI :
              </h3>
              <div className="flex items-start gap-3">
                <p className="text-base sm:text-lg leading-relaxed">
                  Si vous n'avez pas de compte CELI, vous devrez en ouvrir un.
                  Cela peut se faire auprès d'une banque, d'une caisse populaire
                  ou d'autres institutions financières.
                </p>
              </div>
            </div>

            {/* Cotiser régulièrement */}
            <div className="bg-white text-gray-900 rounded-[2.5rem] shadow-lg px-6 sm:px-8 py-7 sm:py-8">
              <h3 className="text-xl sm:text-2xl font-bold text-[#49628b] mb-3">
                Cotiser régulièrement :
              </h3>
              <div className="flex items-start gap-3">
                <p className="text-base sm:text-lg leading-relaxed">
                  Vous pouvez cotiser à votre CELI tout au long de l'année,
                  jusqu'à concurrence de votre plafond de cotisation. Les
                  cotisations peuvent être versées en une somme forfaitaire ou par
                  versements réguliers.
                </p>
              </div>
            </div>
          </div>

          <div className="grid gap-6 sm:gap-8 md:grid-cols-2 mb-10">
            {/* Éviter les cotisations excédentaires */}
            <div className="bg-white text-gray-900 rounded-[2.5rem] shadow-lg px-6 sm:px-8 py-7 sm:py-8">
              <h3 className="text-xl sm:text-2xl font-bold text-[#49628b] mb-3">
                Éviter les cotisations excédentaires :
              </h3>
              <div className="flex items-start gap-3">
                <p className="text-base sm:text-lg leading-relaxed">
                  Assurez-vous de ne pas dépasser votre plafond de cotisation. Les
                  cotisations excédentaires peuvent entraîner des pénalités.
                </p>
              </div>
            </div>

            {/* Surveiller et ajuster */}
            <div className="bg-white text-gray-900 rounded-[2.5rem] shadow-lg px-6 sm:px-8 py-7 sm:py-8">
              <h3 className="text-xl sm:text-2xl font-bold text-[#49628b] mb-3">
                Surveiller et ajuster :
              </h3>
              <div className="flex items-start gap-3">
                <p className="text-base sm:text-lg leading-relaxed">
                  Surveillez régulièrement vos placements et ajustez votre
                  portefeuille en fonction de vos objectifs financiers et des
                  conditions du marché.
                </p>
              </div>
            </div>
          </div>

          {/* Bouton CTA */}
          <div className="flex justify-center">
            <Link
              to="https://book.einsured.ca/widget/bookings/ei-team"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center px-10 py-3 rounded-full bg-[#00b4ff] hover:bg-[#00a3e6] text-[#05203c] font-semibold text-lg shadow-md transition w-full sm:w-auto"
            >
              Commencer
            </Link>
          </div>
        </div>
      </section>

      {/* CRITÈRES D'ADMISSIBILITÉ AU CELI */}
      <section className="py-16 sm:py-20 bg-white px-4 sm:px-6 md:px-20 text-gray-800">
        <div className="max-w-[1380px] mx-auto">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#49628b] text-center mb-6">
            Critères d'admissibilité au CELI :
          </h2>

          <p className="text-base sm:text-lg leading-7 sm:leading-8 mb-8 text-center">
            Les{" "}
            <a
              href="https://www.canada.ca/en/revenue-agency/services/forms-publications/publications/rc4466/tax-free-savings-account-tfsa-guide-individuals.html#P44_1113"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#20a7df] font-medium underline hover:opacity-90"
            >
              critères d'admissibilité
            </a>{" "}
            pour cotiser à un Compte d'épargne libre d'impôt (CELI) au Canada
            comprennent les éléments suivants :
          </p>

          {/* Criteria list */}
          <ul className="space-y-6">
            <li className="flex items-start gap-3">
              <CheckCircle
                aria-hidden="true"
                className="h-6 w-6 text-sky-500 mt-1 flex-shrink-0"
                strokeWidth={2.4}
              />
              <p className="text-base sm:text-lg leading-relaxed">
                <span className="font-bold text-[#49628b]">
                  Condition d'âge :
                </span>{" "}
                Les particuliers doivent être âgés de 18 ans ou plus pour ouvrir
                un CELI et y cotiser.
              </p>
            </li>

            <li className="flex items-start gap-3">
              <CheckCircle
                aria-hidden="true"
                className="h-6 w-6 text-sky-500 mt-1 flex-shrink-0"
                strokeWidth={2.4}
              />
              <p className="text-base sm:text-lg leading-relaxed">
                <span className="font-bold text-[#49628b]">
                  Résidence canadienne :
                </span>{" "}
                Pour être admissible à un CELI, vous devez être résident du
                Canada. Les non-résidents peuvent cotiser à un CELI, mais les
                cotisations effectuées en tant que non-résident seront soumises à
                une taxe de pénalité de 1 % pour chaque mois où la cotisation
                demeure dans le compte.
              </p>
            </li>

            <li className="flex items-start gap-3">
              <CheckCircle
                aria-hidden="true"
                className="h-6 w-6 text-sky-500 mt-1 flex-shrink-0"
                strokeWidth={2.4}
              />
              <p className="text-base sm:text-lg leading-relaxed">
                <span className="font-bold text-[#49628b]">
                  Plafond de cotisation :
                </span>{" "}
                Le gouvernement fixe un plafond de cotisation annuel pour les CELI.
                Les droits de cotisation s'accumulent chaque année et les
                particuliers peuvent reporter les droits inutilisés aux années
                futures. Il est important de respecter ces limites pour éviter des
                pénalités.
              </p>
            </li>

            <li className="flex items-start gap-3">
              <CheckCircle
                aria-hidden="true"
                className="h-6 w-6 text-sky-500 mt-1 flex-shrink-0"
                strokeWidth={2.4}
              />
              <p className="text-base sm:text-lg leading-relaxed">
                <span className="font-bold text-[#49628b]">
                  Droits de cotisation inutilisés :
                </span>{" "}
                Si vous avez des droits de cotisation CELI inutilisés des années
                précédentes, vous pouvez les reporter aux années futures. Cela
                permet aux personnes qui n'ont pas cotisé le montant maximal au
                cours des années précédentes de rattraper leur retard.
              </p>
            </li>

            <li className="flex items-start gap-3">
              <CheckCircle
                aria-hidden="true"
                className="h-6 w-6 text-sky-500 mt-1 flex-shrink-0"
                strokeWidth={2.4}
              />
              <p className="text-base sm:text-lg leading-relaxed">
                <span className="font-bold text-[#49628b]">
                  Types de placements :
                </span>{" "}
                Les CELI peuvent détenir divers types de placements, notamment des
                liquidités, des actions, des obligations, des fonds communs de
                placement et d'autres véhicules de placement admissibles.
              </p>
            </li>
          </ul>
        </div>
      </section>

      {/* OPTIONS DISPONIBLES POUR LE CELI */}
      <section className="py-16 sm:py-2 bg-white px-4 sm:px-6 md:px-20 text-gray-800">
        <div className="max-w-[1380px] mx-auto">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#49628b] text-center mb-6">
            Quelles sont les options disponibles pour le CELI :
          </h2>

          <p className="text-base sm:text-lg leading-7 sm:leading-8 text-center max-w-5xl mx-auto mb-12">
            Un{" "}
            <span className="font-semibold text-[#49628b]">
              Compte d'épargne libre d'impôt (CELI)
            </span>{" "}
            au Canada offre un éventail d'options de placement, permettant aux
            titulaires de comptes de constituer un portefeuille diversifié en
            fonction de leurs objectifs financiers et de leur tolérance au risque.
            Voici quelques options de placement courantes pour le CELI :
          </p>

          <div className="grid gap-8 md:grid-cols-2 mb-8">
            {/* Compte d'épargne */}
            <div className="bg-[#f5f7fb] rounded-3xl border border-gray-200 px-6 sm:px-8 py-8 shadow-sm">
              <h3 className="text-xl sm:text-2xl font-bold text-[#49628b] mb-4">
                Compte d'épargne :
              </h3>
              <div className="flex items-start gap-3">
                <CheckCircle
                  aria-hidden="true"
                  className="h-6 w-6 text-sky-500 mt-1 flex-shrink-0"
                  strokeWidth={2.4}
                />
                <p className="text-base sm:text-lg leading-relaxed">
                  Il s'agit d'une option simple et à faible risque où vous pouvez
                  conserver votre argent dans un compte d'épargne CELI. Bien que
                  les taux d'intérêt puissent être inférieurs à ceux d'autres
                  placements, il offre stabilité et liquidité.
                </p>
              </div>
            </div>

            {/* CPG */}
            <div className="bg-[#f5f7fb] rounded-3xl border border-gray-200 px-6 sm:px-8 py-8 shadow-sm">
              <h3 className="text-xl sm:text-2xl font-bold text-[#49628b] mb-4">
                Certificats de placement garanti (CPG) :
              </h3>
              <div className="flex items-start gap-3">
                <CheckCircle
                  aria-hidden="true"
                  className="h-6 w-6 text-sky-500 mt-1 flex-shrink-0"
                  strokeWidth={2.4}
                />
                <p className="text-base sm:text-lg leading-relaxed">
                  Les CPG sont des placements à terme fixe avec un taux d'intérêt
                  garanti. Ils offrent une approche plus conservatrice, et les
                  intérêts gagnés sont libres d'impôt dans le CELI.
                </p>
              </div>
            </div>
          </div>

          <div className="grid gap-8 md:grid-cols-2 mb-8">
            {/* Actions */}
            <div className="bg-[#f5f7fb] rounded-3xl border border-gray-200 px-6 sm:px-8 py-8 shadow-sm">
              <h3 className="text-xl sm:text-2xl font-bold text-[#49628b] mb-4">
                Actions :
              </h3>
              <div className="flex items-start gap-3">
                <CheckCircle
                  aria-hidden="true"
                  className="h-6 w-6 text-sky-500 mt-1 flex-shrink-0"
                  strokeWidth={2.4}
                />
                <p className="text-base sm:text-lg leading-relaxed">
                  Les CELI vous permettent d'investir dans des actions
                  individuelles. Cette option convient aux personnes à l'aise avec
                  le marché boursier et cherchant une appréciation potentielle du
                  capital.
                </p>
              </div>
            </div>

            {/* Obligations */}
            <div className="bg-[#f5f7fb] rounded-3xl border border-gray-200 px-6 sm:px-8 py-8 shadow-sm">
              <h3 className="text-xl sm:text-2xl font-bold text-[#49628b] mb-4">
                Obligations :
              </h3>
              <div className="flex items-start gap-3">
                <CheckCircle
                  aria-hidden="true"
                  className="h-6 w-6 text-sky-500 mt-1 flex-shrink-0"
                  strokeWidth={2.4}
                />
                <p className="text-base sm:text-lg leading-relaxed">
                  Les obligations sont des titres à revenu fixe qui versent des
                  intérêts périodiques. Elles offrent un rendement plus stable
                  comparé aux actions et peuvent faire partie d'un portefeuille
                  équilibré.
                </p>
              </div>
            </div>
          </div>

          <div className="grid gap-8 md:grid-cols-2 mb-8">
            {/* Fonds communs de placement */}
            <div className="bg-[#f5f7fb] rounded-3xl border border-gray-200 px-6 sm:px-8 py-8 shadow-sm">
              <h3 className="text-xl sm:text-2xl font-bold text-[#49628b] mb-4">
                Fonds communs de placement :
              </h3>
              <div className="flex items-start gap-3">
                <CheckCircle
                  aria-hidden="true"
                  className="h-6 w-6 text-sky-500 mt-1 flex-shrink-0"
                  strokeWidth={2.4}
                />
                <p className="text-base sm:text-lg leading-relaxed">
                  Les fonds communs de placement regroupent l'argent de plusieurs
                  investisseurs pour investir dans un portefeuille diversifié
                  d'actions, d'obligations ou d'autres titres. Les CELI vous
                  permettent de détenir des fonds communs de placement, offrant
                  une diversification instantanée.
                </p>
              </div>
            </div>

            {/* FNB */}
            <div className="bg-[#f5f7fb] rounded-3xl border border-gray-200 px-6 sm:px-8 py-8 shadow-sm">
              <h3 className="text-xl sm:text-2xl font-bold text-[#49628b] mb-4">
                Fonds négociés en bourse (FNB) :
              </h3>
              <div className="flex items-start gap-3">
                <CheckCircle
                  aria-hidden="true"
                  className="h-6 w-6 text-sky-500 mt-1 flex-shrink-0"
                  strokeWidth={2.4}
                />
                <p className="text-base sm:text-lg leading-relaxed">
                  Les FNB sont des fonds de placement négociés en bourse, reflétant
                  la performance d'un indice ou d'une catégorie d'actifs spécifique.
                  Ils offrent une diversification à moindre coût comparé à certains
                  fonds communs de placement.
                </p>
              </div>
            </div>
          </div>

          <div className="grid gap-8 md:grid-cols-2">
            {/* FPI */}
            <div className="bg-[#f5f7fb] rounded-3xl border border-gray-200 px-6 sm:px-8 py-8 shadow-sm">
              <h3 className="text-xl sm:text-2xl font-bold text-[#49628b] mb-4">
                Fiducies de placement immobilier (FPI) :
              </h3>
              <div className="flex items-start gap-3">
                <CheckCircle
                  aria-hidden="true"
                  className="h-6 w-6 text-sky-500 mt-1 flex-shrink-0"
                  strokeWidth={2.4}
                />
                <p className="text-base sm:text-lg leading-relaxed">
                  Les FPI permettent aux investisseurs de détenir une part dans des
                  biens immobiliers générateurs de revenus. Les CELI peuvent
                  détenir des FPI, offrant une exposition au marché immobilier.
                </p>
              </div>
            </div>

            {/* Équivalents de trésorerie */}
            <div className="bg-[#f5f7fb] rounded-3xl border border-gray-200 px-6 sm:px-8 py-8 shadow-sm">
              <h3 className="text-xl sm:text-2xl font-bold text-[#49628b] mb-4">
                Équivalents de trésorerie :
              </h3>
              <div className="flex items-start gap-3">
                <CheckCircle
                  aria-hidden="true"
                  className="h-6 w-6 text-sky-500 mt-1 flex-shrink-0"
                  strokeWidth={2.4}
                />
                <p className="text-base sm:text-lg leading-relaxed">
                  Des placements à court terme et très liquides, tels que les fonds
                  du marché monétaire ou les bons du Trésor, peuvent être détenus
                  dans un CELI.
                </p>
              </div>
            </div>
          </div>

          <p className="mt-10 text-base sm:text-lg leading-relaxed text-center max-w-5xl mx-auto">
            Il est important de noter que le plafond de cotisation au CELI est fixé
            annuellement par le gouvernement, et le dépassement de ces limites peut
            entraîner des pénalités. De plus, les choix de placement individuels
            doivent correspondre à vos objectifs financiers, à votre tolérance au
            risque et à votre horizon temporel.
          </p>
        </div>
      </section>

      {/* DIFFÉRENCE ENTRE UN CELI ET UN REER */}
      <section className="relative bg-[#dce2ec] border-y-4 border-[#2b4672] py-16 sm:py-20 px-4 sm:px-6 md:px-20 mt-16 sm:mt-20">
        <div className="max-w-[1380px] mx-auto">
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#49628b] text-center mb-10">
            Différence entre un CELI et un REER :
          </h2>

          <div className="bg-[#dce2ec] rounded-3xl border border-white/60 shadow-sm px-4 sm:px-8 py-8 sm:py-10">
            {/*  VERSION MOBILE  */}
            <div className="md:hidden">
              {/* Traitement fiscal */}
              <div className="rounded-2xl border border-white/60 bg-white/40 px-4 py-5 mb-5">
                <p className="font-semibold text-[#49628b] text-lg mb-4">
                  Traitement fiscal :
                </p>

                <div className="grid gap-4">
                  <div className="rounded-2xl bg-white px-4 py-4 shadow-sm border border-gray-200">
                    <p className="font-bold text-[#49628b] mb-2">CELI</p>
                    <p className="text-base leading-relaxed text-gray-800">
                      Les cotisations à un CELI sont effectuées avec des dollars
                      après impôt, ce qui signifie que vous ne recevez pas de
                      déduction fiscale pour vos cotisations. Cependant, tout
                      revenu de placement, gain en capital ou retrait d'un CELI
                      est libre d'impôt.
                    </p>
                  </div>

                  <div className="rounded-2xl bg-white px-4 py-4 shadow-sm border border-gray-200">
                    <p className="font-bold text-[#49628b] mb-2">REER</p>
                    <p className="text-base leading-relaxed text-gray-800">
                      Les cotisations à un{" "}
                      <Link
                        to="/fr/regime-epargne-retraite"
                        className="text-[#20a7df] font-medium underline hover:opacity-90"
                      >
                        REER
                      </Link>{" "}
                      sont effectuées avec des dollars avant impôt, et vous
                      recevez une déduction fiscale pour vos cotisations.
                      Cependant, lorsque vous retirez des fonds d'un REER, les
                      retraits sont traités comme un revenu imposable.
                    </p>
                  </div>
                </div>
              </div>

              {/* Droits de cotisation */}
              <div className="rounded-2xl border border-white/60 bg-white/40 px-4 py-5 mb-5">
                <p className="font-semibold text-[#49628b] text-lg mb-4">
                  Droits de cotisation :
                </p>

                <div className="grid gap-4">
                  <div className="rounded-2xl bg-white px-4 py-4 shadow-sm border border-gray-200">
                    <p className="font-bold text-[#49628b] mb-2">CELI</p>
                    <p className="text-base leading-relaxed text-gray-800">
                      Le plafond de cotisation est fixé annuellement par le
                      gouvernement. Les droits de cotisation inutilisés sont
                      reportés aux années futures, offrant une flexibilité pour
                      rattraper les cotisations.
                    </p>
                  </div>

                  <div className="rounded-2xl bg-white px-4 py-4 shadow-sm border border-gray-200">
                    <p className="font-bold text-[#49628b] mb-2">REER</p>
                    <p className="text-base leading-relaxed text-gray-800">
                      Le plafond de cotisation est basé sur votre revenu gagné et
                      est également soumis à un maximum annuel. Les droits de
                      cotisation inutilisés sont reportés et vous pouvez cotiser
                      jusqu'à l'âge de 71 ans.
                    </p>
                  </div>
                </div>
              </div>

              {/* Retraits */}
              <div className="rounded-2xl border border-white/60 bg-white/40 px-4 py-5 mb-5">
                <p className="font-semibold text-[#49628b] text-lg mb-4">
                  Retraits :
                </p>

                <div className="grid gap-4">
                  <div className="rounded-2xl bg-white px-4 py-4 shadow-sm border border-gray-200">
                    <p className="font-bold text-[#49628b] mb-2">CELI</p>
                    <p className="text-base leading-relaxed text-gray-800">
                      Les retraits d'un CELI sont libres d'impôt et peuvent être
                      effectués à tout moment, à n'importe quelle fin, sans
                      affecter les prestations gouvernementales.
                    </p>
                  </div>

                  <div className="rounded-2xl bg-white px-4 py-4 shadow-sm border border-gray-200">
                    <p className="font-bold text-[#49628b] mb-2">REER</p>
                    <p className="text-base leading-relaxed text-gray-800">
                      Les retraits d'un REER sont considérés comme un revenu
                      imposable et peuvent affecter les prestations
                      gouvernementales. Les retraits d'un REER sont soumis à une
                      retenue d'impôt, dont le montant dépend du montant retiré.
                    </p>
                  </div>
                </div>
              </div>

              {/* Objectif */}
              <div className="rounded-2xl border border-white/60 bg-white/40 px-4 py-5 mb-5">
                <p className="font-semibold text-[#49628b] text-lg mb-4">
                  Objectif :
                </p>

                <div className="grid gap-4">
                  <div className="rounded-2xl bg-white px-4 py-4 shadow-sm border border-gray-200">
                    <p className="font-bold text-[#49628b] mb-2">CELI</p>
                    <p className="text-base leading-relaxed text-gray-800">
                      Les CELI sont flexibles et peuvent être utilisés pour divers
                      objectifs financiers, comme épargner pour une maison,
                      l'éducation ou la retraite. Il n'y a aucune restriction sur
                      l'utilisation des fonds.
                    </p>
                  </div>

                  <div className="rounded-2xl bg-white px-4 py-4 shadow-sm border border-gray-200">
                    <p className="font-bold text-[#49628b] mb-2">REER</p>
                    <p className="text-base leading-relaxed text-gray-800">
                      Les REER sont principalement conçus pour l'épargne-retraite.
                      Bien que le Régime d'accession à la propriété (RAP) permette
                      aux acheteurs d'une première maison de retirer de leur REER
                      pour un achat immobilier, l'objectif principal reste
                      l'épargne-retraite à long terme.
                    </p>
                  </div>
                </div>
              </div>

              {/* Limite d'âge */}
              <div className="rounded-2xl border border-white/60 bg-white/40 px-4 py-5">
                <p className="font-semibold text-[#49628b] text-lg mb-4">
                  Limite d'âge :
                </p>

                <div className="grid gap-4">
                  <div className="rounded-2xl bg-white px-4 py-4 shadow-sm border border-gray-200">
                    <p className="font-bold text-[#49628b] mb-2">CELI</p>
                    <p className="text-base leading-relaxed text-gray-800">
                      Il n'y a aucune limite d'âge pour cotiser à un CELI.
                    </p>
                  </div>

                  <div className="rounded-2xl bg-white px-4 py-4 shadow-sm border border-gray-200">
                    <p className="font-bold text-[#49628b] mb-2">REER</p>
                    <p className="text-base leading-relaxed text-gray-800">
                      Les cotisations ne sont pas permises après l'âge de 71 ans,
                      et vous devez convertir le REER en Fonds enregistré de
                      revenu de retraite (FERR) ou en rente.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/*  VERSION BUREAU  */}
            <div className="hidden md:block">
              {/* Ligne d'en-tête */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 border-b border-white/60 pb-6 mb-6 text-[#49628b]">
                <div></div>
                <div className="font-bold text-lg sm:text-xl">CELI</div>
                <div className="font-bold text-lg sm:text-xl">REER</div>
              </div>

              {/* Lignes */}
              <div className="space-y-6 text-base sm:text-lg leading-relaxed text-gray-800">
                {/* Traitement fiscal */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <p className="font-semibold text-[#49628b]">Traitement fiscal :</p>
                  <p>
                    Les cotisations à un CELI sont effectuées avec des dollars
                    après impôt, ce qui signifie que vous ne recevez pas de
                    déduction fiscale pour vos cotisations. Cependant, tout revenu
                    de placement, gain en capital ou retrait d'un CELI est libre
                    d'impôt.
                  </p>
                  <p>
                    Les cotisations à un{" "}
                    <Link
                      to="/fr/regime-epargne-retraite"
                      className="text-[#20a7df] font-medium underline hover:opacity-90"
                    >
                      REER
                    </Link>{" "}
                    sont effectuées avec des dollars avant impôt, et vous recevez
                    une déduction fiscale pour vos cotisations. Cependant, lorsque
                    vous retirez des fonds d'un REER, les retraits sont traités
                    comme un revenu imposable.
                  </p>
                </div>

                {/* Droits de cotisation */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <p className="font-semibold text-[#49628b]">
                    Droits de cotisation :
                  </p>
                  <p>
                    Le plafond de cotisation est fixé annuellement par le
                    gouvernement. Les droits de cotisation inutilisés sont reportés
                    aux années futures, offrant une flexibilité pour rattraper les
                    cotisations.
                  </p>
                  <p>
                    Le plafond de cotisation est basé sur votre revenu gagné et est
                    également soumis à un maximum annuel. Les droits inutilisés sont
                    reportés et vous pouvez cotiser jusqu'à l'âge de 71 ans.
                  </p>
                </div>

                {/* Retraits */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <p className="font-semibold text-[#49628b]">Retraits :</p>
                  <p>
                    Les retraits d'un CELI sont libres d'impôt et peuvent être
                    effectués à tout moment, à n'importe quelle fin, sans affecter
                    les prestations gouvernementales.
                  </p>
                  <p>
                    Les retraits d'un REER sont considérés comme un revenu
                    imposable et peuvent affecter les prestations gouvernementales.
                    Les retraits d'un REER sont soumis à une retenue d'impôt, dont
                    le montant dépend du montant retiré.
                  </p>
                </div>

                {/* Objectif */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <p className="font-semibold text-[#49628b]">Objectif :</p>
                  <p>
                    Les CELI sont flexibles et peuvent être utilisés pour divers
                    objectifs financiers, comme épargner pour une maison,
                    l'éducation ou la retraite. Il n'y a aucune restriction sur
                    l'utilisation des fonds.
                  </p>
                  <p>
                    Les REER sont principalement conçus pour l'épargne-retraite.
                    Bien que le Régime d'accession à la propriété (RAP) permette
                    aux acheteurs d'une première maison de retirer de leur REER
                    pour un achat immobilier, l'objectif principal reste
                    l'épargne-retraite à long terme.
                  </p>
                </div>

                {/* Limite d'âge */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <p className="font-semibold text-[#49628b]">Limite d'âge :</p>
                  <p>Il n'y a aucune limite d'âge pour cotiser à un CELI.</p>
                  <p>
                    Les cotisations ne sont pas permises après l'âge de 71 ans, et
                    vous devez convertir le REER en Fonds enregistré de revenu de
                    retraite (FERR) ou en rente.
                  </p>
                </div>
              </div>
            </div>

            {/* Bouton CTA */}
            <div className="mt-10 flex justify-center">
              <Link
                to="https://book.einsured.ca/widget/bookings/ei-team"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center px-10 py-3 rounded-full bg-[#00b4ff] hover:bg-[#00a3e6] text-[#05203c] font-semibold text-lg shadow-md transition w-full sm:w-auto"
              >
                Investir maintenant
              </Link>
            </div>
          </div>
        </div>
      </section>

      <BlogSection title="Derniers articles" mode="related" relatedCount={3} />
    </>
  );
}
